// My Polls library (ITER-146): which saved polls come along into a new event, and how they arrive

import { collection, doc, writeBatch } from 'firebase/firestore';
import { db } from './firebase';
import type { Poll, PollArrival, PollQuestion, PollTemplate } from './polls';

export interface EventPollFromTemplate {
  eventId: string;
  djId: string;
  title: string;
  questions: PollQuestion[];
  status: 'draft' | 'active';
  mandatory: boolean;
  showToNewUsers: boolean;
  editable: boolean;
  showResultsToAttendees: boolean;
  autoSendOffsetMinutes: number | null;
  fromTemplateId: string;
  createdAt: string;
  responseCount: number;
}

export function selectPollsForNewEvent(templates: PollTemplate[], eventTemplateId: string | null): PollTemplate[] {
  return templates.filter(template =>
    template.isDefault || (eventTemplateId !== null && template.eventTemplateIds.includes(eventTemplateId))
  );
}

export function buildEventPollFromTemplate(
  template: PollTemplate,
  eventId: string,
  djId: string,
  createdAtIso: string
): EventPollFromTemplate {
  return {
    eventId,
    djId,
    title: template.title,
    questions: template.questions,
    status: template.arrival === 'live' ? 'active' : 'draft',
    mandatory: template.settings.mandatory,
    showToNewUsers: template.settings.showToNewUsers,
    editable: template.settings.editable,
    showResultsToAttendees: template.settings.showResultsToAttendees,
    autoSendOffsetMinutes: template.arrival === 'scheduled' ? template.arrivalOffsetMinutes : null,
    fromTemplateId: template.id,
    createdAt: createdAtIso,
    responseCount: 0,
  };
}

export async function addLibraryPollsToEvent(eventId: string, djId: string, templates: PollTemplate[]): Promise<number> {
  if (templates.length === 0) return 0;
  const batch = writeBatch(db);
  const createdAtIso = new Date().toISOString();
  for (const template of templates) {
    batch.set(doc(collection(db, 'polls')), buildEventPollFromTemplate(template, eventId, djId, createdAtIso));
  }
  await batch.commit();
  return templates.length;
}

export function describeOffset(offsetMinutes: number): string {
  if (offsetMinutes === 0) return 'at the start';
  if (offsetMinutes < 0) return `${-offsetMinutes} min before the start`;
  return `${offsetMinutes} min after the start`;
}

export function describeArrival(template: Pick<PollTemplate, 'arrival' | 'arrivalOffsetMinutes'>): string {
  const arrival: PollArrival = template.arrival;
  if (arrival === 'live') return 'Live from the start';
  if (arrival === 'scheduled') return `Sends ${describeOffset(template.arrivalOffsetMinutes)}`;
  return 'Arrives as a draft';
}

function formatSendTime(iso: string): string {
  return new Date(iso).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' });
}

export type AutoSendTone = 'info' | 'warning';

export interface AutoSendDescription {
  text: string;
  tone: AutoSendTone;
}

export function describeAutoSend(poll: Poll): AutoSendDescription | null {
  if (poll.autoSendResult === 'sent') {
    return { text: poll.autoSentAt ? `Sent automatically ${formatSendTime(poll.autoSentAt)}` : 'Sent automatically', tone: 'info' };
  }
  if (poll.status !== 'draft') return null;
  if (poll.autoSendResult === 'noStartTime') {
    return { text: "Couldn't schedule: this event has no start time VoteBeats can read. Send it by hand.", tone: 'warning' };
  }
  if (poll.autoSendResult === 'eventEnded') {
    return { text: 'Not sent: the event had ended.', tone: 'warning' };
  }
  if (typeof poll.autoSendOffsetMinutes !== 'number') return null;
  if (!poll.autoSendDueAt) return { text: 'Scheduling…', tone: 'info' };
  return {
    text: `Sends automatically ${formatSendTime(poll.autoSendDueAt)} (${describeOffset(poll.autoSendOffsetMinutes)})`,
    tone: 'info',
  };
}
